<template>
  <ul class="tree">
    <li
      v-for="(item, key) in items"
      :key="key"
      class="item"
      :class="{ item_with_no_children: !item.children }"
    >
      <button
        class="content"
        :class="{
          'content--regular': !item.children,
        }"
      >
        <span v-if="item.children" class="icon" @click="expandElement(item)">
          <font-awesome-icon
            v-if="item.isOpen"
            :icon="['far', 'chevron-down']"
            fixed-width
          />
          <font-awesome-icon
            v-else
            :icon="['far', 'chevron-right']"
            fixed-width
          />
        </span>
        <span
          :class="{
            'content--root-not-matched':
              item.matches && !item.isRootMatched && !item.isActive,
            'content--active': !!item.isActive,
          }"
          @click="callOnClickHandler(item)"
          >{{ item.name }}</span
        >
      </button>
      <SearchTreeView
        v-if="item.children && item.isOpen"
        :items="getChildren(item)"
        :onClickHandler="onClickHandler"
      />
    </li>
  </ul>
</template>

<script>
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

library.add(faChevronDown, faChevronRight);

export default {
  name: "SearchTreeView",
  components: { FontAwesomeIcon },
  props: {
    /**
     * @vuese
     * Expecting the entire Tree to be passed.
     * A tree consists of a single array.
     * The array is expected to carry items.
     * The items should take the following shape:
     * {
     *   name: String, // the name of the item
     *   children: Array, // an item can carry children which asume the same shape as this described object
     *   isOpen: Boolean, // state to indicate to have the children shown. This state is mutated from inside the component onClick.
     *   isActive: Boolean, // state to color the name blue. Intended for items without children.
     * }
     * */
    items: {
      type: Array,
      required: true,
    },
    /**
     * The callback function that will be invoked when a click event happens
     * It passes one argument; the treeview item object.
     */
    onClickHandler: {
      type: Function,
      required: true,
    },
  },
  methods: {
    callOnClickHandler(item) {
      this.onClickHandler(item);
    },
    expandElement(item) {
      if (typeof item.isOpen === "undefined") return;
      // eslint-disable-next-line no-param-reassign
      item.isOpen = !item.isOpen;
    },
    getChildren(item) {
      return item.matches
        ? item.children.filter((child) => !!child.match)
        : item.children;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../style_variables/style_variables.scss";

.tree {
  list-style: none;
  margin: 0;
  padding: 0 0 0 24px;
}

.item {
  margin: 16px 0;
  word-break: break-word;

  :last-of-type {
    margin-bottom: 0;
  }
}

.item_with_no_children {
  margin: 8px 0;
}

.content {
  cursor: pointer;
  display: inline;
  margin: 0;
  background: none;
  border: none;
  padding: 4px 0;
  width: 100%;
  text-align: start;
  font-family: "Open Sans", sans-serif;
  font-size: 0.875rem;
  display: flex;
  align-items: center;

  &.content--regular {
    padding-left: 24px;
  }
}

.content--active {
  color: $blue;
}

.content--root-not-matched {
  color: $grey_bombay;
}

.icon {
  margin-right: 12px;
  width: 16px;
  height: 16px;
  font-size: 12px;
}
</style>
